import { Component, Show, createEffect, createMemo, createSignal } from 'solid-js'
import {
  NOVEL_IMAGE_MODEL,
  NOVEL_SAMPLER_REV,
  SD_SAMPLER,
  SD_SAMPLER_REV,
} from '../../../../common/image'
import Divider from '../../../shared/Divider'
import RangeInput from '../../../shared/RangeInput'
import Select from '../../../shared/Select'
import TextInput from '../../../shared/TextInput'
import { settingStore, userStore } from '../../../store'
import { IMAGE_SUMMARY_PROMPT } from '/common/image'
import { Toggle } from '/web/shared/Toggle'
import { BaseImageSettings } from '/common/types/image-schema'
import { SolidCard } from '/web/shared/Card'
import { CharEditor } from '../../Character/editor'

export const AIFansImageSettings: Component<{
  cfg?: BaseImageSettings
  inherit?: boolean
  editor: CharEditor
}> = (props) => {
  const state = userStore()
  const settings = settingStore()
  const [type, setType] = createSignal(state.user?.images?.type || 'sd')

  const canUseImages = createMemo(() => {
    const access = state.sub?.tier.imagesAccess || state.user?.admin
    return (
      settings.config.serverConfig?.imagesEnabled &&
      access &&
      settings.config.serverConfig?.imagesModels?.length > 0
    )
  })

  const agnaiModel = createMemo(() => {
    if (!canUseImages()) return
    if (type() !== 'agnai') return

    const id = state.user?.images?.agnai?.model
    return settings.config.serverConfig?.imagesModels?.find((m) => m.name === id)
  })

  const imageTypes = createMemo(() => {
    const list = [
      { label: 'Horde', value: 'horde' },
      { label: 'NovelAI', value: 'novel' },
      { label: 'Stable Diffusion', value: 'sd' },
    ]

    if (canUseImages()) {
      list.push({ label: 'Agnaistic', value: 'agnai' })
    }

    return list
  })

  const subclass = 'flex flex-col gap-4'

  return (
    <div class="flex flex-col gap-4">
      <Select
        fieldName="imageType"
        items={imageTypes()}
        value={(props.inherit ? props.cfg?.type : state.user?.images?.type) ?? 'sd'}
        onChange={(value) => setType(value.value as any)}
        disabled={true}
      />

      <RangeInput
        fieldName="imageSteps"
        min={5}
        max={128}
        step={1}
        value={props.editor.state.imageSettings?.steps ?? 30}
        label="Sampling Steps"
        helperText="(Novel Anlas Threshold: 28)"
        onChange={(ev) =>
          props.editor.update('imageSettings', {
            ...props.editor.state.imageSettings,
            steps: ev,
          })
        }
      />

      <TextInput
        fieldName="imageCfg"
        value={props.editor.state.imageSettings?.cfg}
        label="CFG Scale"
        helperText="Prompt Guidance. Classifier Free Guidance Scale - how strongly the image should conform to prompt - lower values produce more creative results."
        onChange={(ev) =>
          props.editor.update('imageSettings', {
            ...props.editor.state.imageSettings,
            cfg: Number(ev.currentTarget.value),
          })
        }
      />

      <TextInput
        fieldName="imagePrefix"
        value={props.editor.state.imageSettings?.prefix}
        label="Prompt Prefix"
        helperText="(Optional) Text to prepend to your image prompt"
        placeholder={`E.g.: best quality, masterpiece`}
        onChange={(ev) =>
          props.editor.update('imageSettings', {
            ...props.editor.state.imageSettings,
            prefix: ev.currentTarget.value,
          })
        }
      />

      <TextInput
        fieldName="imageSuffix"
        value={props.editor.state.imageSettings?.suffix}
        label="Prompt Suffix"
        helperText="(Optional) Text to append to your image prompt"
        placeholder={`E.g.: full body, visible legs, dramatic lighting`}
        onChange={(ev) =>
          props.editor.update('imageSettings', {
            ...props.editor.state.imageSettings,
            suffix: ev.currentTarget.value,
          })
        }
      />

      <TextInput
        fieldName="imageNegative"
        value={props.editor.state.imageSettings?.negative}
        label="Negative Prompt"
        helperText="(Optional) Negative Prompt"
        placeholder={`E.g.: painting, drawing, illustration, glitch, deformed, mutated, cross-eyed, disfigured`}
        onChange={(ev) =>
          props.editor.update('imageSettings', {
            ...props.editor.state.imageSettings,
            negative: ev.currentTarget.value,
          })
        }
      />

      <Show when={false}>
        <TextInput
          fieldName="summaryPrompt"
          value={props.inherit ? props.cfg?.summaryPrompt : state.user?.images?.summaryPrompt}
          label="Summary Prompt"
          helperText='When summarising the chat to an image caption, this is the "prompt" sent to OpenAI to summarise your conversation into an image prompt.'
          placeholder={`Default: ${IMAGE_SUMMARY_PROMPT.other}`}
        />

        <Toggle
          fieldName="summariseChat"
          label="Summarise Chat"
          helperText="When available use your AI service to summarise the chat into an image prompt. Only available with services with Instruct capabilities (Agnai, NovelAI, OpenAI, Claude, etc)"
          value={props.inherit ? props.cfg?.summariseChat : state.user?.images?.summariseChat}
        />

        <RangeInput
          fieldName="imageWidth"
          min={256}
          max={1280}
          step={128}
          value={
            (props.inherit ? props.cfg?.width : state.user?.images?.width) ??
            agnaiModel()?.init.width ??
            384
          }
          label="Image Width"
          helperText="The larger the image, the less that can be retained in your local cache. (Novel Anlas Threshold: 512)"
          onChange={(ev) => console.log('ev', ev)}
        />

        <RangeInput
          fieldName="imageHeight"
          min={256}
          max={1280}
          step={128}
          value={
            (props.inherit ? props.cfg?.height : state.user?.images?.height) ??
            agnaiModel()?.init.height ??
            384
          }
          label="Image Height"
          helperText="The larger the image, the less that can be retain in your local cache. (Novel Anlas Threshold: 512)"
          onChange={(ev) => console.log('ev', ev)}
        />
      </Show>

      <Show when={!props.inherit}>
        <Divider />

        <div class={type() === 'sd' ? subclass : 'hidden'}>
          <SDSettings />
        </div>
      </Show>
    </div>
  )
}

const SDSettings: Component = () => {
  const state = userStore()

  const samplers = Object.entries(SD_SAMPLER_REV).map(([key, value]) => ({
    label: value,
    value: key,
  }))
  return (
    <>
      <div class="text-xl">Stable Diffusion</div>
      <TextInput
        fieldName="sdUrl"
        label="Stable Diffusion WebUI URL"
        helperText="Base URL for Stable Diffusion. E.g. https://local-tunnel-url-10-20-30-40.loca.lt. If you are self-hosting, you can use http://localhost:7860"
        placeholder="E.g. https://local-tunnel-url-10-20-30-40.loca.lt"
        value={state.user?.images?.sd.url}
      />
      <Select
        fieldName="sdSampler"
        items={samplers}
        label="Sampler"
        value={state.user?.images?.sd.sampler || SD_SAMPLER['DPM++ 2M']}
      />
    </>
  )
}
